<script>

  import {mapActions, mapGetters, mapState} from "vuex";
  import {getNetworkSystemName, getPrettyAddress, prettyAmountWithHideZeros} from "@/public/filters";
  import Icon from "@/public/components/common/icon.vue";
  import CurrencyImg from "@/public/pages/precheckout/components/CurrencyImg.vue";

  export default {
    name: "DashboardSwapConfirmation",
    components: {
      CurrencyImg: () => import(/* webpackChunkName: "public/components/common/uiKit/currencyImg" */ '/public/components/common/uiKit/currencyImg'),
      Icon
    },
    data() {
      return {
        processing: false
      }
    },
    props: {
      commissionLoading: {
        type: Boolean,
        default: false
      },
      feeToken: {
        type: Object,
        default: () => ({})
      },
      timer: {
        type: Number,
        default: undefined
      },
      swapRate: {
        type: [Number, String],
        default: undefined
      },
      addressFromObj: {
        type: Object,
        default: () => ({})
      },
      addressToObj: {
        type: Object,
        default: () => ({})
      }
    },
    computed: {
      ...mapGetters('app', [
        'isOpenModal',
      ]),
      ...mapState('app', {
        payload: state => state.modal.payload,
        networks: state => state.networks,
        currencies: state => state.currencies,
      }),
      ...mapState('accounts', ['accounts']),
      accountId({ accounts }) {
        return accounts.find(({ accountId }) => !!accountId).accountId
      },
      amountFrom() {
        return this.feeToken.amountFrom
      },
      amountTo() {
        return this.feeToken.amountTo
      },
      addressFrom() {
        return this.addressFromObj ? getPrettyAddress(this.addressFromObj.address) : undefined
      },
      blockchainFee({ feeToken = {} }) {
        if (feeToken.blockchainFeeFromUSD && feeToken.blockchainFeeToUSD) {
          return Number(feeToken.blockchainFeeFromUSD) + Number(feeToken.blockchainFeeToUSD)
        }
      },
      currencyAndNetworkFrom() {
        const network = this.networks.find((item) => item.id === this.addressFromObj.networkId)
        const currency = this.currencies.find((item) => item.id === this.addressFromObj.currencyId)

        return {
          ...this.addressFromObj,
          network,
          currency,
        }
      },
      currencyAndNetworkTo() {
        const network = this.networks.find((item) => item.id === this.addressToObj.networkId)
        const currency = this.currencies.find((item) => item.id === this.addressToObj.currencyId)

        return {
          ...this.addressToObj,
          network,
          currency,
        }
      },
      currencyFrom() {
        const { currency } = this.currencyAndNetworkFrom || {}

        return currency
      },
      currencyTo() {
        const { currency } = this.currencyAndNetworkTo || {}

        return currency
      },
      currencyFromName() {
        const { currency } = this.currencyAndNetworkFrom || {}

        return currency?.currency
      },
      currencyToName() {
        const { currency } = this.currencyAndNetworkTo || {}

        return currency?.currency
      },
      networkFrom() {
        const { network } = this.currencyAndNetworkFrom || {}
        return network
      },
      networkFromName({ networkFrom = {} }) {
        return networkFrom?.name
      },
      networkTo() {
        return this.feeToken.networkToId || {}
      },
      networkToName({ networkTo = {} }) {
        return getNetworkSystemName(networkTo)
      },
      addressTo() {
        return this.addressToObj ? getPrettyAddress(this.addressToObj.address) : undefined
      },
      isOpen() {
        const {
          $options: {
            name,
          },
          isOpenModal,
        } = this;

        return isOpenModal(name);
      },
      serviceCommission() {
        return this.feeToken.serviceFeeUSD
      }
    },
    methods: {
      prettyAmountWithHideZeros,
      ...mapActions('app', [
        'closeModal',
      ]),
      ...mapActions('swap', [
        'setSwapTransfer'
      ]),
      // handleClose() {
      //   this.closeModal();
      // },
      async confirm() {
        this.processing = true

        const payload = {
          accountId: this.accountId,
          clientId: null,
          addressFromId: this.payload.addressFrom.id,
          addressToId: this.payload.addressTo.id,
          token: this.feeToken.token,
        }

        const result = await this.setSwapTransfer(payload)

        this.processing = false

        this.$emit("swap-result", result)
        // this.handleClose()
      }
    }
  }
</script>

<template>
<div class="swap-modal-wrapper main-modal-body">
  <div class="first-line">
    <div class="info-item long">
      <div class="label">
        {{ $t('swap.modals.execute.currency') }}
      </div>
      <div class="value">
        <currency-img :currency="currencyFromName"/>
        <span class="currency-alias">
                {{ currencyFrom?.alias }}
              </span>
        <span class="currency-ticker">
                {{ (networkFromName || '') | getNetworkShortNameByNetworkName }}
              </span>
      </div>
    </div>
    <div class="info-item">
      <div class="label">
        {{ $t('swap.modals.execute.amount') }}
      </div>
      <div class="value">
        {{ prettyAmountWithHideZeros(amountFrom) }}
      </div>
    </div>
    <div class="info-item">
      <div class="label">
        {{ $t('swap.modals.execute.address') }}
      </div>
      <div class="value">
        {{ addressFrom }}
      </div>
    </div>
  </div>
  <div class="cross-chain-modal-divider">
    <div class="line separator">
      <div class="separator-line"></div>
      <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M4.08119 5.71946C2.6665 7.66661 2.6665 10.4442 2.6665 15.9993C2.6665 21.5545 2.6665 24.3321 4.08119 26.2792C4.53808 26.9081 5.0911 27.4611 5.71995 27.918C7.6671 29.3327 10.4447 29.3327 15.9998 29.3327C21.555 29.3327 24.3326 29.3327 26.2797 27.918C26.9086 27.4611 27.4616 26.9081 27.9185 26.2792C29.3332 24.3321 29.3332 21.5545 29.3332 15.9993C29.3332 10.4442 29.3332 7.66661 27.9185 5.71946C27.4616 5.09061 26.9086 4.53759 26.2797 4.0807C24.3326 2.66602 21.555 2.66602 15.9998 2.66602C10.4447 2.66602 7.6671 2.66602 5.71995 4.0807C5.0911 4.53759 4.53808 5.09061 4.08119 5.71946ZM18.9035 8.44169C19.3226 8.02094 19.8344 7.62288 20.5158 7.51431C20.6556 7.49204 20.7968 7.48083 20.9382 7.48083C21.0797 7.48083 21.2208 7.49204 21.3606 7.51431C22.042 7.62288 22.5538 8.02094 22.973 8.44168C23.3769 8.84723 23.8228 9.40955 24.3397 10.0615L25.7595 11.8522C26.1407 12.3331 26.06 13.0319 25.5792 13.4132C25.0983 13.7945 24.3995 13.7137 24.0182 13.2329L22.6376 11.4917C22.413 11.2084 22.2198 10.9651 22.0493 10.7555L22.0493 22.4191C22.0493 23.0328 21.5519 23.5302 20.9382 23.5302C20.3246 23.5302 19.8271 23.0328 19.8271 22.4191L19.8271 10.7555C19.6567 10.9651 19.4634 11.2085 19.2388 11.4917L17.8583 13.2329C17.477 13.7137 16.7781 13.7945 16.2973 13.4132C15.8164 13.0319 15.7357 12.3331 16.117 11.8522L17.5368 10.0616C18.0537 9.40957 18.4995 8.84724 18.9035 8.44169ZM12.1727 9.57935C12.1727 8.9657 11.6752 8.46824 11.0616 8.46824C10.4479 8.46824 9.95045 8.9657 9.95045 9.57935L9.95046 21.2429C9.78003 21.0333 9.58675 20.79 9.36218 20.5068L7.98159 18.7656C7.60033 18.2847 6.90146 18.204 6.42062 18.5853C5.93978 18.9665 5.85905 19.6654 6.24031 20.1462L7.66012 21.9369C8.17703 22.5889 8.62285 23.1512 9.02684 23.5568C9.44596 23.9775 9.95777 24.3756 10.6392 24.4841C10.779 24.5064 10.9201 24.5176 11.0616 24.5176C11.203 24.5176 11.3442 24.5064 11.484 24.4841C12.1654 24.3756 12.6772 23.9775 13.0963 23.5568C13.5003 23.1512 13.9461 22.5889 14.463 21.9369L15.8828 20.1462C16.2641 19.6654 16.1834 18.9665 15.7025 18.5853C15.2217 18.204 14.5228 18.2847 14.1415 18.7656L12.761 20.5068C12.5364 20.79 12.3431 21.0333 12.1727 21.2429L12.1727 9.57935Z" fill="#744DB2"/>
      </svg>
      <div class="separator-line"></div>
    </div>
  </div>
  <div class="second-line">
    <div class="info-item long">
      <div class="label">
        {{ $t('swap.modals.execute.currency') }}
      </div>
      <div class="value">
        <currency-img :currency="currencyToName"/>
        <span class="currency-alias">
          {{ currencyTo?.alias }}
        </span>
        <span class="currency-ticker">
          {{ (networkToName || '') | getNetworkShortNameByNetworkName }}
        </span>
      </div>
    </div>
    <div class="info-item">
      <div class="label">
        {{ $t('swap.modals.execute.amount') }}
      </div>
      <div class="value">
        {{ prettyAmountWithHideZeros(amountTo) }}
      </div>
    </div>
    <div class="info-item">
      <div class="label">
        {{ $t('swap.modals.execute.address') }}
      </div>
      <div class="value">
        {{ addressTo }}
      </div>
    </div>
  </div>
  <div class="commissions">
    <div class="line">
      <div class="amount">
        <div class="left">{{ $t('swap.modals.execute.service_commission') }}</div>
        <div class="right">{{ !!feeToken.serviceFeeUSD ? prettyAmountWithHideZeros( Number(feeToken.serviceFeeUSD), 3 ) : 0 }} USD</div>
      </div>
      <div class="source">
        <div class="left">{{ $t('swap.modals.execute.source') }}</div>
        <div class="right">{{ $t('swap.modals.execute.advance_balance') }}</div>
      </div>
    </div>
    <div class="line">
      <div class="amount">
        <div class="left">{{ $t('swap.modals.execute.network_commission') }}</div>
        <div class="right">{{ !!blockchainFee ? prettyAmountWithHideZeros(blockchainFee, 3) : 0 }} USD</div>
      </div>
      <div class="source">
        <div class="left">{{ $t('swap.modals.execute.source') }}</div>
        <div class="right">{{ $t('swap.modals.execute.swap_amount') }}</div>
      </div>
    </div>
    <div class="line">
      <div class="amount">
        <div class="left">{{ $t('swap.modals.execute.exchange_rate') }}</div>
        <div class="right">
          1 {{ addressFromObj.currency }} = {{ swapRate }} {{ addressToObj.currencyId | getCurrencyTicker }}
        </div>
      </div>
    </div>
    <div class="line last">
      <div class="amount">
        <div class="left">{{ $t('swap.modals.execute.you_receive') }}</div>
        <div class="right">
          ≈ {{ amountTo }} {{ currencyToName }}
        </div>
      </div>
    </div>
  </div>
</div>
</template>

<style lang="scss" scoped>

.loading {
  .main-modal-header {
    opacity: 0.5
  }
  .main-modal-body {
    opacity: 0.5
  }
  .main-modal-footer {
    opacity: 0.5;
    pointer-events: none;
  }
}


.main-modal-body {
  position: relative;

  font-family: var(--new-front-font-family);
  padding: 0;

  .cross-chain-modal-divider {
    position: relative;

    width: 100%;
    margin: 15px 0;
  }

  .first-line, .second-line {
    display: flex;
    align-items: start;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;

    padding: 0 30px;

    .info-item {
      width: 22%;

      &.long {
        width: 43%;
      }

      .label, .value {
        display: flex;
        align-items: center;

        gap: 7px;

        font-style: normal;
      }
      .label {
        color: var(--new-front-text-secondary-bg-modal-primary);
        font-size: $h6;
        line-height: $h6;
        margin-bottom: 10px;

        font-weight: 400;
      }
      .value {
        color: var(--new-front-text-primary-bg-modal-primary);
        font-size: $h4;
        line-height: $h4;

        font-weight: 500;

        .currency-ticker {
          color: var(--new-front-text-secondary-bg-modal-primary)
        }
      }
    }
  }
  .first-line {
    padding: 35px 30px 0 30px;
  }

  .commissions {
    padding: 0 30px;
    margin-top: 40px;
    border-top: 1px dashed var(--border-devider-primary);

    .line {
      display: flex;
      flex-direction: column;
      gap: 5px;

      padding: 15px 0;
      border-bottom: 1px solid var(--border-devider-primary);

      font-size: $h4;
      font-weight: 500;

      .amount, .source {
        display: flex;
        align-items: center;
        justify-content: space-between;
        .left {
          text-align: left;
        }
        .right {
          text-align: right;
        }
      }
      .amount {
        color: var(--new-front-text-primary-bg-modal-primary);
      }
      .source {
        color: var(--new-front-text-secondary-bg-modal-primary);
      }
    }
  }
}
.separator-line {
  background: var(--border-devider-primary);
  height: 1px;
  width: 100%;
}
.separator {
  display: grid;
  grid-template-columns: 1fr 32px 1fr;
  align-items: center;
  gap: 10px;
  padding: 0 30px;
  svg {
    width: 32px;
    height: 32px;
  }
}

@include below_phone() {
  .main-modal-header {
    font-size: $h3;
  }
  .main-modal-body {
    .first-line, .second-line {
      padding: 10px 30px;

      .info-item {
        .label {
          font-size: $h8;
          line-height: $h8;
        }
        .value {
          font-size: $h6;
          line-height: $h6;
        }
      }
    }
    .commissions {
      .line {
        padding: 10px 30px;

        font-size: $h6;
        line-height: $h6;
      }
    }
  }
}
</style>
